/**
 * Subscription Widget
 * 
 * This is for the subscription widget block on the product page.
 * It handles the selection of subscription plans, adding to cart, and updating the UI accordingly.
 * 
 * Dispatches:
 *  - ThemeEvents.cartUpdate
 */
import { ThemeEvents } from "@theme/events";
import { formatCurrency } from "@theme/utilities";

class SubscriptionWidget {
  #currentSubPrice = null;

  constructor(root) {
    this.root = root;
    this.section = this.root.closest('.shopify-section');
    this.cta = root.querySelector('[data-msw-add-subscription]');
    this.ctaText = this.cta?.querySelector('.btn__text');
    this.ctaSpinner = this.cta?.querySelector('.add-to-cart-spinner');
    this.onetimeLink = root.querySelector('[data-msw-add-onetime]');
    this.onetimePrice = root.querySelector('[data-msw-onetime-price]');
    this.priceMap = root.querySelector('[data-msw-price-map]');

    this.#initSelectedPlan();
    this.#bindCards();
    this.#bindGiftsToggles();
    this.#bindAddToCart();
    this.#bindVariantChange();
    this.#initPagePrice();
    this.#observeProductPrice();
  }
  
// Sync the root's selling-plan-id from the pre-checked radio on load,
  // so the CTA submits a valid plan before any card click or variant change.
  #initSelectedPlan() {
    const checked = this.root.querySelector('.msw__radio:checked');
    if (checked) this.root.dataset.sellingPlanId = checked.value;
  }

  #activateCard(card) {
    const input = card.querySelector('.msw__radio');
    input.checked = true;
    this.root.querySelectorAll('[data-msw-card]').forEach(c => c.classList.remove('is-active'));
    card.classList.add('is-active');
    this.root.dataset.sellingPlanId = input.value;

    // Re-run price update so per-serving price reflects the newly selected plan
    this.#updatePrices({ id: Number(this.root.dataset.variantId) });

  }

  // Shows/hides plan cards to match which plans the given variant actually
  // sells, re-picking the active card if the previously active plan is no
  // longer offered for this variant.
  #syncVisiblePlans(variantData) {
    const cards = [...this.root.querySelectorAll('[data-msw-card]')];
    let firstValidCard = null;

    cards.forEach(card => {
      const isValid = variantData.plans?.[card.dataset.planId] != null;
      card.hidden = !isValid;
      if (isValid && !firstValidCard) firstValidCard = card;
    });

    const activePlanId = this.root.dataset.sellingPlanId;
    const activeCardStillValid = cards.some(c => c.dataset.planId === activePlanId && !c.hidden);

    if (!activeCardStillValid && firstValidCard) {
      const input = firstValidCard.querySelector('.msw__radio');
      input.checked = true;
      cards.forEach(c => c.classList.remove('is-active'));
      firstValidCard.classList.add('is-active');
      this.root.dataset.sellingPlanId = input.value;
    }
  }

  #bindCards() {
    this.root.querySelectorAll('[data-msw-card]').forEach(card => {
      card.addEventListener('click', e => {
        if (!e.target.closest('[data-msw-gifts-toggle]')) this.#activateCard(card);
      });
      card.querySelector('.msw__radio').addEventListener('change', () => this.#activateCard(card));
    });
  }

  #bindGiftsToggles() {
    this.root.querySelectorAll('[data-msw-gifts-toggle]')?.forEach(toggle => {
      toggle.addEventListener('click', () => {
        toggle.setAttribute('aria-expanded', toggle.getAttribute('aria-expanded') !== 'true');
        document.getElementById(toggle.getAttribute('aria-controls'))?.toggleAttribute('hidden');
      });
    });
  }

  async #addToCart(withPlan) {
    if (!this.cta) return;

    this.cta.disabled = true;
    this.cta.classList.add('btn--loading');
    this.ctaSpinner?.classList.remove('hidden');

    const cartDrawer = document.querySelector('cart-drawer-component');
    const cartSectionId = cartDrawer?.dataset.sectionId ?? 'cart-drawer';

    const body = {
      id: Number(this.root.dataset.variantId),
      quantity: 1,
      sections: cartSectionId,
      ...(withPlan && { selling_plan: Number(this.root.dataset.sellingPlanId) }),
    };

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Accept': 'application/json' 
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();

      document.dispatchEvent(new CustomEvent(ThemeEvents.cartUpdate, {
        detail: {
          data: { 
            itemCount: 1,
            isIncremental: true,
            sections: data.sections
          },
        },
      }));
    } catch {
      if (this.ctaText) this.ctaText.textContent = 'Something went wrong';
    } finally {
      this.cta.disabled = false;
      this.cta.classList.remove('btn--loading');
      this.ctaSpinner?.classList.add('hidden');
    }
  }

  #bindAddToCart() {
    this.cta.addEventListener('click', () => this.#addToCart(true));
    this.onetimeLink?.addEventListener('click', e => { 
      e.preventDefault();
      if (this.onetimeLink.getAttribute('aria-disabled') === 'true') return;
      this.#addToCart(false); 
    });
  }

  #bindVariantChange() {
    document.addEventListener(ThemeEvents.variantChanged, ({ detail: { variant } }) => {
      const disabled = !variant.available;
      this.root.dataset.variantId = String(variant.id);
      this.cta.disabled = disabled;
      this.cta.setAttribute('aria-disabled', disabled);
      if (this.ctaText) this.ctaText.textContent = disabled ? 'Sold out' : this.cta.dataset.label;

      if (this.onetimeLink) {
        this.onetimeLink.setAttribute('aria-disabled', disabled);
        this.onetimeLink.tabIndex = disabled ? -1 : 0;
      }

      this.#updatePrices(variant);
    });
  }

  #updatePrices(variant) {
    if (!this.priceMap) return;

    let priceMap;

    try {
      priceMap = JSON.parse(this.priceMap.textContent);
    } catch {
      return;
    }

    const variantData = priceMap[String(variant.id)];
    if (!variantData) return;

    this.#syncVisiblePlans(variantData);

    const moneyFormat = window.FoxTheme?.moneyFormat ?? '£{{amount}}';

    // Subscription price per plan card (skipped if a block-level override removed the data attribute)
    this.root.querySelectorAll('[data-msw-plan-price]')?.forEach(el => {
      const planData = variantData.plans?.[el.dataset.mswPlanPrice];
      if (planData?.price != null) el.textContent = formatCurrency(planData.price, moneyFormat);
    });

    // Strikethrough price (skipped if override removed the data attribute)
    this.root.querySelectorAll('[data-msw-strike-price]')?.forEach(el => {
      el.hidden = !variantData.compareAtPrice;
      if (variantData.compareAtPrice) {
        el.textContent = formatCurrency(variantData.compareAtPrice, moneyFormat);
      }
    });

    // Per-serving price (uses the active plan's allocated price ÷ servings)
    const activePlanId = this.root.dataset.sellingPlanId;

    this.root.querySelectorAll('[data-msw-serving-price]')?.forEach(el => {
      const planData = variantData.plans?.[activePlanId];
      if (planData?.servingPrice != null) {
        el.textContent = formatCurrency(planData.servingPrice, moneyFormat);
      }
    });

    // Keep page-level price in sync with active plan's subscription price
    const planData = variantData.plans?.[activePlanId];
    if (planData?.price != null) {
      this.#currentSubPrice = planData.price;
      this.#applySubPriceToPage();
    }

    // One-time price in link
    if (this.onetimePrice) this.onetimePrice.textContent = formatCurrency(variantData.price, moneyFormat);
  }

  // Apply the stored subscription price to the page-level product-price element
  #applySubPriceToPage() {
    const priceEl = this.section?.querySelector('product-price');

    if (!priceEl || this.#currentSubPrice == null) return;

    const moneyFormat = window.FoxTheme?.moneyFormat ?? '£{{amount}}';
    const formatted = formatCurrency(this.#currentSubPrice, moneyFormat);
    const target = priceEl.querySelector('.price--sale') 
      ?? priceEl.querySelector('.price__regular .price');

    if (target && target.textContent.trim() !== formatted) {
      target.textContent = formatted;
    }
  }

  // Apply the subscription price before any variant change fires
  #initPagePrice() {
    const variantId = this.root.dataset.variantId;
    const planId = this.root.dataset.sellingPlanId;
    let priceMap;

    try { 
      priceMap = JSON.parse(this.priceMap?.textContent);
    } catch { 
      return;
    }

    const subPrice = priceMap?.[variantId]?.plans?.[planId]?.price;

    if (subPrice == null) return;

    this.#currentSubPrice = subPrice;
    this.#applySubPriceToPage();
  }

  // Watches the product-price element for DOM mutations and re-applies
  // the subscription price each time it gets overwritten
  #observeProductPrice() {
    const priceEl = this.section?.querySelector('product-price');
    if (!priceEl) return;

    new MutationObserver(() => this.#applySubPriceToPage())
      .observe(priceEl, { subtree: true, childList: true });
  }
}

document.querySelectorAll('[data-msw]').forEach(root => new SubscriptionWidget(root));